import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

export default class extends Controller {
  static targets = [
    "textarea",
    "splitTextarea",
    "preview",
    "splitPreview",
    "writeView",
    "previewView",
    "splitView",
    "writeTab",
    "previewTab",
    "splitTab",
    "container",
    "wordCount",
    "charCount",
    "readingTime"
  ]

  static values = {
    placeholder: String
  }

  connect() {
    this.currentView = "write"
    this.marked = marked
    this.marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: true,
      mangle: false,
      sanitize: false
    })

    this.updateStats()
    this.renderPreview()
  }

  // View switching
  switchToWrite() {
    this.currentView = "write"
    this.updateViewClasses()
    this.writeViewTarget.classList.remove("hidden")
    this.previewViewTarget.classList.add("hidden")
    this.splitViewTarget.classList.add("hidden")
    this.syncToMain()
  }

  switchToPreview() {
    this.currentView = "preview"
    this.updateViewClasses()
    this.renderPreview()
    this.writeViewTarget.classList.add("hidden")
    this.previewViewTarget.classList.remove("hidden")
    this.splitViewTarget.classList.add("hidden")
  }

  switchToSplit() {
    this.currentView = "split"
    this.updateViewClasses()
    this.renderSplitPreview()
    this.writeViewTarget.classList.add("hidden")
    this.previewViewTarget.classList.add("hidden")
    this.splitViewTarget.classList.remove("hidden")
    this.syncToSplit()
  }

  updateViewClasses() {
    const activeClass = ["bg-[#E4E5E8]", "text-[#030712]"]
    const inactiveClass = ["text-[#6B7280]", "hover:text-[#030712]", "hover:bg-[#E4E5E8]"]

    ;[
      [this.writeTabTarget, "write"],
      [this.previewTabTarget, "preview"],
      [this.splitTabTarget, "split"]
    ].forEach(([tab, view]) => {
      tab.classList.remove(...activeClass, ...inactiveClass)
      if (this.currentView === view) {
        tab.classList.add(...activeClass)
      } else {
        tab.classList.add(...inactiveClass)
      }
    })
  }

  // Input handling
  handleInput() {
    this.updateStats()
    if (this.currentView === "split") {
      this.syncToSplit()
      this.renderSplitPreview()
    }
  }

  handleSplitInput() {
    this.updateStats()
    this.syncToMain()
    this.renderSplitPreview()
  }

  syncToSplit() {
    if (this.hasSplitTextareaTarget) {
      this.splitTextareaTarget.value = this.textareaTarget.value
    }
  }

  syncToMain() {
    if (this.hasTextareaTarget && this.hasSplitTextareaTarget) {
      this.textareaTarget.value = this.splitTextareaTarget.value
    }
  }

  // Stats
  updateStats() {
    const text = this.getCurrentText()
    const words = text.trim() ? text.trim().split(/\s+/).length : 0
    const chars = text.length
    const readingTime = Math.max(1, Math.ceil(words / 200))

    this.wordCountTarget.textContent = `${words} word${words !== 1 ? "s" : ""}`
    this.charCountTarget.textContent = `${chars} character${chars !== 1 ? "s" : ""}`
    this.readingTimeTarget.textContent = `~${readingTime} min read`
  }

  getCurrentText() {
    if (this.currentView === "split" && this.hasSplitTextareaTarget) {
      return this.splitTextareaTarget.value
    }
    return this.textareaTarget.value
  }

  // Preview rendering
  renderPreview() {
    const text = this.textareaTarget.value
    this.previewTarget.innerHTML = this.marked.parse(text)
  }

  renderSplitPreview() {
    const text = this.currentView === "split" 
      ? this.splitTextareaTarget.value 
      : this.textareaTarget.value
    this.splitPreviewTarget.innerHTML = this.marked.parse(text)
  }

  // Keyboard shortcuts
  handleKeydown(event) {
    // Ctrl/Cmd + B = Bold
    if ((event.ctrlKey || event.metaKey) && event.key === "b") {
      event.preventDefault()
      this.formatBold()
    }
    // Ctrl/Cmd + I = Italic
    if ((event.ctrlKey || event.metaKey) && event.key === "i") {
      event.preventDefault()
      this.formatItalic()
    }
    // Ctrl/Cmd + K = Link
    if ((event.ctrlKey || event.metaKey) && event.key === "k") {
      event.preventDefault()
      this.formatLink()
    }
  }

  // Formatting actions
  formatBold() {
    this.wrapSelection("**", "**", "bold text")
  }

  formatItalic() {
    this.wrapSelection("*", "*", "italic text")
  }

  formatStrikethrough() {
    this.wrapSelection("~~", "~~", "strikethrough")
  }

  formatHeading1() {
    this.insertAtStartOfLine("# ")
  }

  formatHeading2() {
    this.insertAtStartOfLine("## ")
  }

  formatHeading3() {
    this.insertAtStartOfLine("### ")
  }

  formatBulletList() {
    this.insertAtStartOfLine("- ")
  }

  formatNumberedList() {
    this.insertAtStartOfLine("1. ")
  }

  formatTaskList() {
    this.insertAtStartOfLine("- [ ] ")
  }

  formatLink() {
    this.wrapSelection("[", "](url)", "link text")
  }

  formatImage() {
    this.wrapSelection("![", "](image-url)", "alt text")
  }

  formatCode() {
    this.wrapSelection("`", "`", "code")
  }

  formatCodeBlock() {
    this.wrapSelection("```\n", "\n```", "// code here")
  }

  formatQuote() {
    this.insertAtStartOfLine("> ")
  }

  formatTable() {
    const tableTemplate = "| Header 1 | Header 2 |\n| -------- | -------- |\n| Cell 1   | Cell 2   |\n| Cell 3   | Cell 4   |"
    this.insertAtCursor(tableTemplate)
  }

  // Helper methods
  getTextarea() {
    if (this.currentView === "split") {
      return this.splitTextareaTarget
    }
    return this.textareaTarget
  }

  wrapSelection(before, after, placeholder) {
    const textarea = this.getTextarea()
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value
    const selection = text.substring(start, end)

    const replacement = selection.length > 0 
      ? before + selection + after 
      : before + placeholder + after

    textarea.setRangeText(replacement, start, end, "end")
    
    // If no selection, place cursor inside
    if (selection.length === 0) {
      const cursorPos = start + before.length
      textarea.setSelectionRange(cursorPos, cursorPos + placeholder.length)
    }

    textarea.focus()
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  insertAtStartOfLine(insertion) {
    const textarea = this.getTextarea()
    const start = textarea.selectionStart
    const text = textarea.value
    
    // Find start of current line
    let lineStart = text.lastIndexOf("\n", start - 1) + 1
    if (lineStart === -1) lineStart = 0

    const newText = text.substring(0, lineStart) + insertion + text.substring(lineStart)
    textarea.value = newText
    
    textarea.setSelectionRange(start + insertion.length, start + insertion.length)
    textarea.focus()
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  insertAtCursor(text) {
    const textarea = this.getTextarea()
    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    textarea.setRangeText(text, start, end, "end")
    textarea.focus()
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
